import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/App";
import TopNav from "@/components/TopNav";
import AllBaiseLogo from "@/components/AllBaiseLogo";
import {
  Building2, Globe, Mail, MapPin, Heart, MessageSquare,
  Share2, Users, ChevronRight, Send, Loader2, ExternalLink,
  Check, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function PublicCorporateLanding() {
  const { subdomain: paramSubdomain } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();

  // Detect subdomain from URL or param
  const hostname = window.location.hostname;
  const subdomain = paramSubdomain || (
    hostname.includes("allbaise.com") && hostname !== "allbaise.com" && hostname !== "www.allbaise.com"
      ? hostname.split(".allbaise.com")[0].split(".").pop()
      : null
  );

  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [postingComment, setPostingComment] = useState(false);
  const [isMember, setIsMember] = useState(false);

  useEffect(() => {
    if (subdomain) fetchTenant();
    else setLoading(false);
  }, [subdomain]);

  const fetchTenant = async () => {
    try {
      const { data } = await axios.get(`${API}/tenant/public/${subdomain}`);
      setTenant(data);
      // Check membership
      if (auth?.user) {
        try {
          const memberRes = await axios.get(`${API}/tenant/my-membership`, { headers: getHeaders() });
          if (memberRes.data.is_member && memberRes.data.tenant?.subdomain === subdomain) {
            setIsMember(true);
          }
        } catch {}
        // Check follow status
        try {
          const followersRes = await axios.get(`${API}/tenant/public/${subdomain}/followers`);
          const isFollowing = followersRes.data.followers?.some(
            f => f.user_id === auth.user.user_id
          );
          setFollowing(isFollowing);
        } catch {}
        // Load comments
        fetchComments();
      } else {
        fetchComments();
      }
    } catch (err) {
      setTenant(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      const { data } = await axios.get(`${API}/tenant/public/${subdomain}/comments`);
      setComments(data.comments || []);
    } catch {}
  };

  const handleFollow = async () => {
    if (!auth?.user) {
      navigate("/landing");
      return;
    }
    try {
      const { data } = await axios.post(
        `${API}/tenant/public/${subdomain}/follow`,
        {},
        { headers: getHeaders() }
      );
      setFollowing(data.following);
      toast.success(data.message);
      // Refresh to update count
      fetchTenant();
    } catch (err) {
      toast.error("Failed to toggle follow");
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!auth?.user) {
      navigate("/landing");
      return;
    }
    if (!newComment.trim()) return;

    setPostingComment(true);
    try {
      await axios.post(
        `${API}/tenant/public/${subdomain}/comments`,
        { content: newComment.trim() },
        { headers: getHeaders() }
      );
      setNewComment("");
      toast.success("Comment posted!");
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post comment");
    } finally {
      setPostingComment(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
        <TopNav />
        <div className="container mx-auto px-4 py-20 text-center">
          <Building2 className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Corporate Not Found</h1>
          <p className="text-muted-foreground mb-6">
            {subdomain
              ? `${subdomain}.allbaise.com doesn't exist or is inactive`
              : "No corporate subdomain detected"}
          </p>
          <Button onClick={() => navigate("/")}>Go Home</Button>
        </div>
      </div>
    );
  }

  // If user IS a member of this tenant, redirect to workspace
  if (isMember) {
    navigate(`/dashboard`);
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <TopNav />

      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border-b">
        <div className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-6 flex-col md:flex-row">
              {/* Company Avatar */}
              <div className="w-20 h-20 md:w-28 md:h-28 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                {tenant.logo_url ? (
                  <img src={tenant.logo_url} alt={tenant.corporate_name} className="w-full h-full rounded-xl object-cover" />
                ) : (
                  <Building2 className="w-10 h-10 md:w-14 md:h-14 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{tenant.corporate_name}</h1>
                <p className="text-muted-foreground mb-4">
                  {tenant.description || `${tenant.corporate_name} on AllBaise`}
                </p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {tenant.follower_count || 0} followers
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-4 h-4" />
                    {tenant.comment_count || 0} comments
                  </span>
                  <Badge variant="outline" className="text-xs">
                    <Globe className="w-3 h-3 mr-1" />
                    {tenant.subdomain}.allbaise.com
                  </Badge>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-8">
              <Button
                onClick={handleFollow}
                variant={following ? "secondary" : "default"}
                className="gap-2"
              >
                <Heart className={`w-4 h-4 ${following ? "fill-current text-red-500" : ""}`} />
                {following ? "Following" : "Follow"}
              </Button>
              <Button variant="outline" className="gap-2" onClick={() => {
                if (!auth?.user) { navigate("/landing"); return; }
                navigate(`/corporate-hub/${tenant.subdomain}`);
              }}>
                <ExternalLink className="w-4 h-4" />
                View Profile
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Left: Company Info */}
          <div className="md:col-span-2 space-y-6">
            {/* About */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-3">About</h2>
              <p className="text-muted-foreground">
                {tenant.description || "This corporate is on AllBaise — an AI-powered enterprise platform."}
              </p>
            </Card>

            {/* Services */}
            {tenant.services?.length > 0 && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-3">Services</h2>
                <div className="flex flex-wrap gap-2">
                  {tenant.services.map((svc, i) => (
                    <Badge key={i} variant="secondary">{svc}</Badge>
                  ))}
                </div>
              </Card>
            )}

            {/* Comments Section */}
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Comments</h2>

              {/* Comment Form */}
              {auth?.user ? (
                <form onSubmit={handleComment} className="flex gap-2 mb-6">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    maxLength={1000}
                    className="flex-1"
                  />
                  <Button type="submit" disabled={postingComment || !newComment.trim()}>
                    {postingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </form>
              ) : (
                <div className="mb-6 p-4 bg-muted/30 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-2">
                    Sign in to leave a comment
                  </p>
                  <Button variant="outline" size="sm" onClick={() => navigate("/landing")}>
                    Sign In
                  </Button>
                </div>
              )}

              {/* Comments List */}
              {comments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No comments yet. Be the first to comment!
                </p>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="border-b border-border pb-3 last:border-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{comment.user_name}</span>
                        <span className="text-xs text-muted-foreground">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {formatDate(comment.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Right: Contact Info Sidebar */}
          <div className="space-y-4">
            <Card className="p-4">
              <h3 className="font-medium mb-3">Contact</h3>
              <div className="space-y-3 text-sm">
                {tenant.contact_email && (
                  <a href={`mailto:${tenant.contact_email}`}
                     className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                    <Mail className="w-4 h-4" />
                    {tenant.contact_email}
                  </a>
                )}
                {tenant.website && (
                  <a href={tenant.website} target="_blank" rel="noopener noreferrer"
                     className="flex items-center gap-2 text-muted-foreground hover:text-primary">
                    <Globe className="w-4 h-4" />
                    Website
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </Card>

            {/* Quick Actions */}
            <Card className="p-4">
              <h3 className="font-medium mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start gap-2" onClick={handleFollow}>
                  <Heart className={`w-4 h-4 ${following ? "fill-current text-red-500" : ""}`} />
                  {following ? "Unfollow" : "Follow"}
                </Button>
                <Button variant="outline" className="w-full justify-start gap-2" onClick={() => {
                  if (!auth?.user) { navigate("/landing"); return; }
                  toast.success("Request sent to corporate admin");
                }}>
                  <Mail className="w-4 h-4" />
                  Request Access
                </Button>
              </div>
            </Card>

            {/* Stats */}
            <Card className="p-4">
              <h3 className="font-medium mb-3">Stats</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Followers</span>
                  <span className="font-medium">{tenant.follower_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Comments</span>
                  <span className="font-medium">{tenant.comment_count || 0}</span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
